<script setup lang="ts">
import { computed } from 'vue'

import { APP_ICON_PATHS, isDirectionalAppIcon, type AppIconName } from '@/lib/icons'

/**
 * Module iconography. Decorative by default — an icon that repeats the label
 * beside it must not be announced twice — and named only when it is the sole
 * content of a control.
 */

const props = withDefaults(
  defineProps<{
    name: AppIconName
    size?: number
    label?: string
    strokeWidth?: number
    /**
     * Whether the glyph mirrors under `dir="rtl"`. Defaults to the icon's own
     * nature; set it explicitly where the layout, not the reading direction,
     * decides. `undefined` is the declared default on purpose — Vue casts an
     * absent Boolean prop to `false`, which would stop every chevron flipping.
     */
    flipRtl?: boolean | undefined
  }>(),
  { size: 16, label: '', strokeWidth: 1.75, flipRtl: undefined },
)

const path = computed(() => APP_ICON_PATHS[props.name])
const decorative = computed(() => props.label === '')
const mirrors = computed(() => props.flipRtl ?? isDirectionalAppIcon(props.name))
</script>

<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    :stroke="'currentColor'"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    :aria-hidden="decorative ? 'true' : undefined"
    :role="decorative ? undefined : 'img'"
    :aria-label="decorative ? undefined : label"
    class="app-icon"
    :class="{ 'app-icon--directional': mirrors }"
  >
    <path :d="path" />
  </svg>
</template>

<style>
.app-icon {
  flex: none;
  display: inline-block;
  vertical-align: middle;
}

/* Only glyphs that point along the reading axis mirror; see lib/icons.ts. */
[dir='rtl'] .app-icon--directional {
  transform: scaleX(-1);
}
</style>
